import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserAssignedCourses } from '../lib/api';

const PrivateRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [assignedCourses, setAssignedCourses] = useState<any[]>([]);

  useEffect(() => {
    const checkPermissions = async () => {
      if (user) {
        try {
          console.log('开始获取用户权限...');
          console.log('当前用户:', user);
          // 调用API获取用户权限
          const response = await getUserAssignedCourses(user.id);
          console.log('API响应:', response);
          const assignedCourses = response.data;
          console.log('分配的课程:', assignedCourses);
          
          // 存储分配的课程到组件状态
          setAssignedCourses(assignedCourses);
          
          // 收集所有权限类型
          const permissions = new Set<string>();
          
          // 遍历所有课程、阶段和课时，收集权限
          assignedCourses.forEach(course => {
            console.log('课程:', course.course_title);
            course.stages?.forEach(stage => {
              console.log('  阶段:', stage.stage_title);
              stage.lessons?.forEach(lesson => {
                console.log('    课时:', lesson.lesson_title, '权限:', lesson.permission_type);
                permissions.add(lesson.permission_type);
              });
            });
          });
          
          const permissionArray = Array.from(permissions);
          console.log('用户权限:', permissionArray);
          
          // 核心判断逻辑
          // 根据用户权限选择跳转页面
          const hasEditPermission = permissionArray.includes('edit_develop');
          const hasViewPermission = permissionArray.includes('view_only');
          
          // 存储权限到组件状态
          setUserPermissions(permissionArray);
          console.log('用户权限:', permissionArray);
          
          if (user.role === 'sys' || hasEditPermission) {
            // sys角色或有编辑权限 -> 跳转到开发平台
            console.log('有编辑权限或sys角色，跳转到/dashboard');
            setRedirectPath('/dashboard');
          } else if (hasViewPermission) {
            // 只有查看权限 -> 跳转到看课平台
            console.log('只有查看权限，跳转到/student/portal');
            setRedirectPath('/student/portal');
          } else {
            // 没有权限 -> 保持在当前位置
            console.log('没有权限，保持在当前位置');
            setRedirectPath(null);
          }
        } catch (permissionErr: any) {
          // 权限API调用失败时的处理
          console.error('获取用户权限失败:', permissionErr);
          
          // 设置默认权限
          const defaultPermissions: string[] = [];
          setUserPermissions(defaultPermissions);
          setAssignedCourses([]);
          
          // 根据用户角色默认跳转
          if (user.role === 'sys') {
            // sys角色默认跳转到开发平台
            setRedirectPath('/dashboard');
          } else if (user.role === 'teacher' || user.role === 'teacher_developer') {
            // 老师默认跳转到开发平台
            setRedirectPath('/dashboard');
          } else {
            // 其他角色默认跳转到看课平台
            setRedirectPath('/student/portal');
          }
        } finally {
          setPermissionChecked(true);
        }
      } else {
        setPermissionChecked(true);
      }
    };

    if (user && !permissionChecked) {
      checkPermissions();
    }
  }, [user, permissionChecked]);

  if (loading) {
    // 只有在真正加载时才显示加载动画
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // 加载完成后，如果没有用户信息，重定向到登录页
  console.log('=== PrivateRoute 验证步骤 1: 登录状态检查 ===');
  if (!user) {
    console.log('未登录用户，重定向到登录页');
    return <Navigate to="/login" replace />;
  }
  console.log('登录状态检查通过，用户已登录');

  // 定义不需要重定向的路径白名单
  console.log('=== PrivateRoute 验证步骤 2: 路径白名单检查 ===');
  const noRedirectPaths = [
    '/student/portal',
    '/student/lesson',
    '/student/vibe-coding',
    '/settings',
    '/admin',
    '/course',
    '/workshop'
  ];
  console.log('白名单路径:', noRedirectPaths);

  // 检查当前路径是否在白名单中
  const isInNoRedirectPath = noRedirectPaths.some(path => 
    location.pathname === path || location.pathname.startsWith(path + '/')
  );
  console.log('当前路径:', location.pathname);
  console.log('是否在白名单中:', isInNoRedirectPath);

  // 权限检查完成后，根据权限决定跳转路径
  console.log('=== PrivateRoute 验证步骤 3: 权限重定向检查 ===');
  console.log('权限检查状态:', permissionChecked);
  console.log('重定向路径:', redirectPath);
  console.log('当前路径与重定向路径是否不同:', location.pathname !== redirectPath);
  console.log('是否不在白名单中:', !isInNoRedirectPath);
  
  // 只有当用户不在目标路径且当前路径不在白名单中时才执行重定向，避免无限循环
  if (permissionChecked && redirectPath && location.pathname !== redirectPath && !isInNoRedirectPath) {
    console.log('执行重定向到:', redirectPath);
    return <Navigate to={redirectPath} replace />;
  }
  console.log('重定向检查通过，继续执行');

  // 权限检查：根据用户角色和权限验证当前路径的访问权限
  console.log('=== PrivateRoute 验证步骤 4: 路径权限检查 ===');
  if (permissionChecked) {
    const currentPath = location.pathname;
    const hasEditPermission = userPermissions.includes('edit_develop') || false;
    const hasViewPermission = userPermissions.includes('view_only') || false;
    
    // 检查用户是否为老师角色（包括teacher和teacher_developer）
    const isTeacher = user?.role === 'teacher' || user?.role === 'teacher_developer';
    
    console.log('=== 权限检查详细信息 ===');
    console.log('当前用户:', user);
    console.log('当前路径:', currentPath);
    console.log('用户权限:', userPermissions);
    console.log('hasEditPermission:', hasEditPermission);
    console.log('hasViewPermission:', hasViewPermission);
    console.log('isTeacher:', isTeacher);
    
    // 1. 只有sys角色的用户才能访问/admin、/admin/*和/settings
    console.log('=== 权限检查子步骤 1: 管理员路径检查 ===');
    if ((currentPath === '/admin' || currentPath.startsWith('/admin/') || currentPath === '/settings') && user?.role !== 'sys') {
      console.log('无权限访问/admin或/settings，重定向');
      const redirectTarget = hasEditPermission ? '/dashboard' : '/student/portal';
      console.log('重定向目标:', redirectTarget);
      return <Navigate to={redirectTarget} replace />;
    }
    console.log('管理员路径检查通过');
    
    // 2. sys和有edit_develop权限的老师才能访问/dashboard、/course和/workshop
    console.log('=== 权限检查子步骤 2: 开发平台路径检查 ===');
    if ((currentPath === '/dashboard' || currentPath.startsWith('/dashboard/') || 
         currentPath === '/course' || currentPath.startsWith('/course/') ||
         currentPath === '/workshop' || currentPath.startsWith('/workshop/'))) {
      console.log('检查开发平台路径权限...');
      console.log('用户角色:', user?.role);
      console.log('是否为老师:', isTeacher);
      console.log('是否有编辑权限:', hasEditPermission);
      
      // 检查用户是否有基本的edit_develop权限
      if (!(user?.role === 'sys' || (isTeacher && hasEditPermission))) {
        console.log('无权限访问开发平台路径，重定向到:/student/portal');
        return <Navigate to='/student/portal' replace />;
      }
      
      // 对于workshop路径，检查用户是否对当前课程有edit_develop权限
      if (currentPath.startsWith('/workshop/')) {
        console.log('检查workshop路径的课程权限...');
        
        // 从URL中提取lessonId
        const workshopMatch = currentPath.match(/\/workshop\/(plan|ware)\/\d+\/(\d+)\/\d+/);
        if (workshopMatch) {
          const lessonId = parseInt(workshopMatch[2]);
          console.log('提取的lessonId:', lessonId);
          
          // 检查用户是否对该课程有edit_develop权限
          const hasLessonPermission = assignedCourses.some(course => {
            return course.stages?.some((stage: any) => {
              return stage.lessons?.some((lesson: any) => {
                return lesson.lesson_id === lessonId && lesson.permission_type === 'edit_develop';
              });
            });
          });
          
          console.log('用户是否对该课程有edit_develop权限:', hasLessonPermission);
          
          if (!hasLessonPermission && user?.role !== 'sys') {
            console.log('无权限访问该课程的workshop页面，重定向到:/dashboard');
            return <Navigate to='/dashboard' replace />;
          }
        }
      }
    }
    console.log('开发平台路径检查通过');
    
    // 3. /course路径的权限检查在CourseDetailPage中进行
    console.log('=== 权限检查子步骤 3: 课程路径检查 ===');
    if (currentPath === '/course' || currentPath.startsWith('/course/')) {
      console.log('/course路径权限检查将在CourseDetailPage中进行');
      console.log('继续访问路径:', currentPath);
    }
    console.log('课程路径检查通过');
    
    // 4. 有edit_develop和view_only权限的都能访问/student/portal和/student/vibe-coding
    console.log('=== 权限检查子步骤 4: 学生路径检查 ===');
    if ((currentPath === '/student/portal' || currentPath === '/student/vibe-coding' || 
         currentPath.startsWith('/student/portal/') || currentPath.startsWith('/student/vibe-coding/')) && 
        !hasEditPermission && !hasViewPermission) {
      console.log('无权限访问学生路径，重定向到登录页');
      return <Navigate to='/login' replace />;
    }
    console.log('学生路径检查通过');
    
    console.log('=== 权限检查完成 ===');
    console.log('权限检查通过，继续访问当前路径:', currentPath);
  } else {
    console.log('权限检查尚未完成，继续访问');
  }

  // 权限检查完成且不需要重定向，或者正在检查权限时，显示子路由
  return <Outlet />;
};

export default PrivateRoute;